import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { signalColourFor, signalStateAt, type SignalColour } from '../world/signals';
import type { CityLayout, SignalVisuals } from '../world/types';

/**
 * Traffic-signal controller. Timing is pure data on the layout (`Intersection.signal`) and is
 * evaluated with `signalStateAt` / `signalColourFor` from simulation time, so the phase of every
 * junction is a pure function of `ctx.state.time` — deterministic, rewindable, no accumulated state.
 *
 * The controller adds change notification on top: it remembers the last colour of every approach
 * and, when one changes, notifies subscribers and the optional `signalVisuals` service.
 */

export interface SignalApproach {
  intersectionId: number;
  edgeId: number;
  colour: SignalColour;
  /** Seconds until this approach's colour next changes (phase boundary), for AI anticipation. */
  changesIn: number;
}

export type SignalListener = (intersectionId: number, edgeId: number, colour: SignalColour) => void;

export interface SignalService {
  /** Colour shown to traffic entering `intersectionId` along `edgeId` at sim time `time` (default: now). */
  colourFor(intersectionId: number, edgeId: number, time?: number): SignalColour;
  /** Seconds until the colour for that approach changes. Infinity for unsignalled junctions. */
  changesIn(intersectionId: number, edgeId: number, time?: number): number;
  /** Every signalled approach with its current colour. */
  approaches(): readonly SignalApproach[];
  /** Subscribe to colour changes; returns an unsubscribe function. */
  onChange(listener: SignalListener): () => void;
}

declare module '../core/services' {
  interface ServiceMap {
    signals: SignalService;
  }
}

export class SignalController implements SignalService {
  private readonly listeners = new Set<SignalListener>();
  private readonly list: SignalApproach[] = [];
  private visuals: SignalVisuals | null = null;
  private announced = false;

  constructor(
    readonly layout: CityLayout,
    private readonly clock: () => number,
  ) {
    for (const ix of layout.intersections) {
      if (!ix.signal) continue;
      // Every edge that appears in some phase, plus any edge meeting the junction.
      for (const edgeId of ix.edgeIds) {
        this.list.push({ intersectionId: ix.id, edgeId, colour: signalColourFor(ix, edgeId, 0), changesIn: Infinity });
      }
    }
  }

  get currentVisuals(): SignalVisuals | null {
    return this.visuals;
  }

  setVisuals(v: SignalVisuals | null): void {
    this.visuals = v;
    this.announced = false;
  }

  colourFor(intersectionId: number, edgeId: number, time: number = this.clock()): SignalColour {
    const ix = this.layout.intersections[intersectionId];
    return ix ? signalColourFor(ix, edgeId, time) : 'green';
  }

  changesIn(intersectionId: number, edgeId: number, time: number = this.clock()): number {
    const ix = this.layout.intersections[intersectionId];
    if (!ix || !ix.signal) return Infinity;
    const sig = ix.signal;
    const now = signalColourFor(ix, edgeId, time);
    const st = signalStateAt(sig, time);
    // Walk forward through phases until the colour for this edge differs.
    let remaining = st.remaining;
    let idx = st.phaseIndex;
    for (let i = 0; i < sig.phases.length; i++) {
      idx = (idx + 1) % sig.phases.length;
      const phase = sig.phases[idx]!;
      const c: SignalColour = phase.green.includes(edgeId) ? 'green' : phase.amber.includes(edgeId) ? 'amber' : 'red';
      if (c !== now) return remaining;
      remaining += phase.duration;
    }
    return Infinity;
  }

  approaches(): readonly SignalApproach[] {
    return this.list;
  }

  onChange(listener: SignalListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Re-evaluate all approaches; notify on change. Call every few fixed steps. */
  update(): void {
    const t = this.clock();
    for (const a of this.list) {
      const ix = this.layout.intersections[a.intersectionId]!;
      const colour = signalColourFor(ix, a.edgeId, t);
      a.changesIn = this.changesIn(a.intersectionId, a.edgeId, t);
      if (colour !== a.colour || !this.announced) {
        a.colour = colour;
        this.visuals?.setColour(a.intersectionId, a.edgeId, colour);
        for (const l of this.listeners) l(a.intersectionId, a.edgeId, colour);
      }
    }
    this.announced = true;
  }
}

/** World-band system (order 120): provides the `signals` service and drives `signalVisuals`. */
export function createSignalSystem(): System {
  let controller: SignalController;
  let ctx: GameContext;
  return {
    id: 'signals',
    order: 120,
    init(c) {
      ctx = c;
      controller = new SignalController(c.services.get('layout'), () => c.state.time);
      c.services.provide('signals', controller);
      c.hooks.registerSnapshot('signals', () => ({
        count: controller.approaches().length,
        approaches: controller.approaches().slice(0, 48).map((a) => ({ i: a.intersectionId, e: a.edgeId, c: a.colour, in: Number.isFinite(a.changesIn) ? +a.changesIn.toFixed(1) : null })),
      }));
    },
    start() {
      controller.setVisuals(ctx.services.tryGet('signalVisuals') ?? null);
      controller.update();
    },
    fixedUpdate() {
      // Visual providers may be registered after our start(): pick them up lazily.
      if (ctx.state.frame % 30 === 0) {
        const v = ctx.services.tryGet('signalVisuals') ?? null;
        if (v !== null && v !== controller.currentVisuals) controller.setVisuals(v);
      }
      if (ctx.state.frame % 3 === 0) controller.update();
    },
  };
}
